import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ClipboardList, Plus, Edit2, Trash2, Copy, Target, Calendar, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface TreatmentPlanTemplatesProps {
  clinicianId: string;
  clientId?: string;
  clientName?: string;
  onApplyTemplate?: (template: TreatmentTemplate) => void;
}

interface TreatmentTemplate {
  id: string;
  title: string;
  description: string;
  category: string;
  duration_weeks: number;
  goals: TreatmentGoal[];
  interventions: string[];
  review_frequency: string;
  created_at: string;
}

interface TreatmentGoal {
  id: string;
  title: string;
  target_date?: string;
  metrics?: string;
  status: 'pending' | 'in_progress' | 'achieved';
}

const DEFAULT_TEMPLATES: Omit<TreatmentTemplate, 'id' | 'created_at'>[] = [
  {
    title: "Anxiety Management Programme",
    description: "Structured approach to managing generalised anxiety using CBT techniques",
    category: "anxiety",
    duration_weeks: 12,
    goals: [
      { id: "1", title: "Identify anxiety triggers and patterns", status: "pending" },
      { id: "2", title: "Learn and practise grounding techniques", status: "pending" },
      { id: "3", title: "Develop healthy coping strategies", status: "pending" },
      { id: "4", title: "Reduce avoidance behaviours by 50%", status: "pending" }
    ],
    interventions: [
      "Cognitive restructuring",
      "Progressive muscle relaxation",
      "Exposure therapy (gradual)",
      "Mindfulness-based stress reduction",
      "Breathing exercises"
    ],
    review_frequency: "fortnightly"
  },
  {
    title: "Depression Recovery Programme",
    description: "Comprehensive treatment plan for moderate depression",
    category: "depression",
    duration_weeks: 16,
    goals: [
      { id: "1", title: "Establish daily routine and structure", status: "pending" },
      { id: "2", title: "Increase pleasant activities to 3 per week", status: "pending" },
      { id: "3", title: "Challenge negative thought patterns", status: "pending" },
      { id: "4", title: "Improve sleep hygiene", status: "pending" },
      { id: "5", title: "Build social connections", status: "pending" }
    ],
    interventions: [
      "Behavioural activation",
      "Cognitive therapy",
      "Sleep hygiene education",
      "Exercise prescription",
      "Social skills training"
    ],
    review_frequency: "weekly"
  },
  {
    title: "Trauma Processing Programme",
    description: "EMDR and stabilisation-focused trauma treatment",
    category: "trauma",
    duration_weeks: 20,
    goals: [
      { id: "1", title: "Establish safety and stabilisation", status: "pending" },
      { id: "2", title: "Develop window of tolerance", status: "pending" },
      { id: "3", title: "Process traumatic memories safely", status: "pending" },
      { id: "4", title: "Integrate experience and rebuild meaning", status: "pending" }
    ],
    interventions: [
      "Psychoeducation about trauma",
      "Grounding and containment techniques",
      "EMDR processing",
      "Parts work",
      "Somatic experiencing"
    ],
    review_frequency: "fortnightly"
  },
  {
    title: "Life Coaching - Goal Achievement",
    description: "Structured coaching programme for personal goal setting and achievement",
    category: "coaching",
    duration_weeks: 8,
    goals: [
      { id: "1", title: "Clarify personal values and vision", status: "pending" },
      { id: "2", title: "Set SMART goals for key life areas", status: "pending" },
      { id: "3", title: "Identify and overcome limiting beliefs", status: "pending" },
      { id: "4", title: "Create action plan with accountability", status: "pending" }
    ],
    interventions: [
      "Values clarification exercises",
      "Wheel of Life assessment",
      "Goal setting frameworks",
      "Accountability check-ins",
      "Visualisation techniques"
    ],
    review_frequency: "weekly"
  },
  {
    title: "Relationship Improvement Programme",
    description: "Focus on attachment patterns and communication skills",
    category: "relationships",
    duration_weeks: 12,
    goals: [
      { id: "1", title: "Understand attachment style and patterns", status: "pending" },
      { id: "2", title: "Improve communication skills", status: "pending" },
      { id: "3", title: "Set healthy boundaries", status: "pending" },
      { id: "4", title: "Develop conflict resolution strategies", status: "pending" }
    ],
    interventions: [
      "Attachment style assessment",
      "Non-violent communication training",
      "Boundary setting exercises",
      "Couples or interpersonal therapy techniques",
      "Emotion regulation skills"
    ],
    review_frequency: "fortnightly"
  }
];

export function TreatmentPlanTemplates({ clinicianId, clientId, clientName, onApplyTemplate }: TreatmentPlanTemplatesProps) {
  const [templates, setTemplates] = useState<TreatmentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TreatmentTemplate | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<TreatmentTemplate | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    duration_weeks: 12,
    goals: [] as TreatmentGoal[],
    interventions: [] as string[],
    review_frequency: 'fortnightly'
  });

  const [newGoal, setNewGoal] = useState('');
  const [newIntervention, setNewIntervention] = useState('');

  useEffect(() => {
    loadTemplates();
  }, [clinicianId]);

  const loadTemplates = async () => {
    // For now, use default templates. In production, these would be stored in the database
    const templatesWithIds = DEFAULT_TEMPLATES.map((t, i) => ({
      ...t,
      id: `default-${i}`,
      created_at: new Date().toISOString()
    }));
    setTemplates(templatesWithIds);
    setLoading(false);
  };

  const handleAddGoal = () => {
    if (!newGoal.trim()) return;
    setFormData(f => ({
      ...f,
      goals: [...f.goals, { id: crypto.randomUUID(), title: newGoal.trim(), status: 'pending' as const }]
    }));
    setNewGoal('');
  };

  const handleRemoveGoal = (goalId: string) => {
    setFormData(f => ({
      ...f,
      goals: f.goals.filter(g => g.id !== goalId)
    }));
  };

  const handleAddIntervention = () => {
    if (!newIntervention.trim()) return;
    setFormData(f => ({
      ...f,
      interventions: [...f.interventions, newIntervention.trim()]
    }));
    setNewIntervention('');
  };

  const handleRemoveIntervention = (index: number) => {
    setFormData(f => ({
      ...f,
      interventions: f.interventions.filter((_, i) => i !== index)
    }));
  };

  const handleApply = (template: TreatmentTemplate) => {
    if (onApplyTemplate) {
      onApplyTemplate(template);
      toast.success(`Applied "${template.title}" to ${clientName}`);
    }
    setSelectedTemplate(null);
  };

  const getCategoryColour = (category: string) => {
    switch (category) {
      case 'anxiety':
        return 'bg-secondary/20 text-secondary border-secondary/30';
      case 'depression':
        return 'bg-primary/20 text-primary border-primary/30';
      case 'trauma':
        return 'bg-destructive/20 text-destructive border-destructive/30';
      case 'coaching':
        return 'bg-accent/20 text-accent-foreground border-accent/30';
      case 'relationships':
        return 'bg-muted/50 text-foreground border-muted';
      default:
        return 'bg-muted/30 text-foreground border-border';
    }
  };

  if (loading) {
    return (
      <div className="flex items-centre justify-centre p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card/50 border-border/30">
        <CardHeader>
          <div className="flex items-centre justify-between">
            <div>
              <CardTitle className="flex items-centre gap-2 text-foreground">
                <ClipboardList className="h-5 w-5 text-primary" />
                Treatment Plan Templates
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {clientId 
                  ? `Select a template to apply to ${clientName}'s treatment plan`
                  : 'Pre-built treatment frameworks for common presentations'
                }
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreate(true)} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
              <Plus className="h-4 w-4" />
              Create Template
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates.map(template => (
              <Card 
                key={template.id} 
                className="bg-card border-border/30 cursor-pointer hover:border-primary/50 transition-colours"
                onClick={() => setSelectedTemplate(template)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <Badge variant="outline" className={getCategoryColour(template.category)}>
                      {template.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {template.duration_weeks} weeks
                    </span>
                  </div>
                  <CardTitle className="text-lg text-foreground">{template.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">{template.description}</p>
                  <div className="flex items-centre gap-4 text-xs text-muted-foreground">
                    <span className="flex items-centre gap-1">
                      <Target className="h-3 w-3" />
                      {template.goals.length} goals
                    </span>
                    <span className="flex items-centre gap-1">
                      <Calendar className="h-3 w-3" />
                      Review {template.review_frequency}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Template Detail Dialog */}
      <Dialog open={!!selectedTemplate} onOpenChange={(open) => !open && setSelectedTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-centre gap-2 text-foreground">
              {selectedTemplate?.title}
              <Badge variant="outline" className={getCategoryColour(selectedTemplate?.category || '')}>
                {selectedTemplate?.category}
              </Badge>
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {selectedTemplate?.description}
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="flex-1 pr-4">
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/20 rounded-lg border border-border/30">
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="font-semibold text-foreground">{selectedTemplate?.duration_weeks} weeks</p>
                </div>
                <div className="p-3 bg-muted/20 rounded-lg border border-border/30">
                  <p className="text-xs text-muted-foreground">Review Frequency</p>
                  <p className="font-semibold text-foreground capitalize">{selectedTemplate?.review_frequency}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3 flex items-centre gap-2 text-foreground">
                  <Target className="h-4 w-4 text-primary" />
                  Treatment Goals
                </h4>
                <div className="space-y-2">
                  {selectedTemplate?.goals.map((goal, i) => (
                    <div key={goal.id} className="flex items-centre gap-2 p-2 bg-muted/20 rounded border border-border/30">
                      <CheckCircle2 className="h-4 w-4 text-primary" />
                      <span className="text-sm text-foreground">{goal.title}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3 text-foreground">Interventions</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedTemplate?.interventions.map((intervention, i) => (
                    <Badge key={i} variant="outline" className="border-border text-foreground">
                      {intervention}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" className="border-border text-foreground hover:bg-muted/20">Close</Button>
            </DialogClose>
            {clientId && onApplyTemplate && (
              <Button onClick={() => selectedTemplate && handleApply(selectedTemplate)} className="gap-2 bg-primary text-primary-foreground hover:bg-primary/90">
                <Copy className="h-4 w-4" />
                Apply to {clientName}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
